import Add from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFiltersContext } from '../../../context/FiltersContext';
import type { FiltersContext } from '../../../context/types';
import { FILTER_TYPES, type FilterCriteria, type FilterValue } from '../../types';
import { countActiveMoreFilters } from '../../utils';
import { FiltersBarButton } from '../FiltersBarButton';
import { MoreFiltersMenu } from './MoreFiltersMenu';

type MoreFiltersProps<Criteria extends FilterCriteria> = {
  filters: FiltersContext<Criteria>['filtersConfig'];
};

export const MoreFilters = <Criteria extends Record<string, string | string[]>>({
  filters,
}: MoreFiltersProps<Criteria>) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { criteria, onFiltersChange, disabledFilters, defaultFilters, isFilteringDisabled } =
    useFiltersContext<Criteria>();
  const [tempCriteria, setTempCriteria] = useState<AllTableCriteria<Criteria>>(criteria);

  const activeFiltersCount = useMemo(
    () => countActiveMoreFilters(filters, criteria.filters),
    [filters, criteria.filters]
  );

  const hasActiveFilters = activeFiltersCount > 0;

  const handleOpenMenu = useCallback((e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleCloseAndReset = () => {
    handleCloseMenu();
    setTempCriteria(criteria);
  };

  const handleClearFilters = () => {
    const preservedFilterKeys = [
      ...disabledFilters,
      'phrase',
      ...filters.filter(filter => filter.clearable === false).map(filter => filter.key),
    ];

    const moreFiltersKeys = filters.reduce<string[]>((acc, filter) => {
      if (filter.clearable === false) {
        return acc;
      }

      if (filter.type === FILTER_TYPES.MULTI_OPTION_WITH_RANGE && filter.options && filter.options.length > 0) {
        filter.options.forEach(option => {
          acc.push(option.id);
        });
        return acc;
      } else {
        acc.push(filter.key);
        return acc;
      }
    }, []);

    const clearedFilters = Object.keys(tempCriteria.filters || {}).reduce<Record<string, FilterValue | undefined>>(
      (acc, key) => {
        const isInMoreFilters = moreFiltersKeys.includes(key);
        const isPreserved = preservedFilterKeys.includes(key);

        if (!(isInMoreFilters && !isPreserved)) {
          acc[key] = tempCriteria.filters?.[key];
        }

        return acc;
      },
      {}
    );

    const mergedFilters = {
      ...(defaultFilters ?? {}),
      ...clearedFilters,
    } as unknown as Criteria;

    const updatedCriteria = {
      ...tempCriteria,
      filters: mergedFilters,
    } as AllTableCriteria<Criteria>;

    onFiltersChange(updatedCriteria.filters!);
    handleCloseAndReset();
  };

  const handleApply = () => {
    onFiltersChange(tempCriteria.filters!);
    handleCloseMenu();
  };

  const handleOptionChange = (key: string, options: FilterValue) => {
    setTempCriteria(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [key]: Array.isArray(options) ? (options.length > 0 ? options : undefined) : options,
      } as Criteria,
    }));
  };

  const handleOptionChangeImmediately = useCallback(
    (key: string, options: FilterValue) => {
      onFiltersChange({
        ...criteria.filters,
        [key]: Array.isArray(options) ? (options.length > 0 ? options : undefined) : options,
      } as Criteria);
    },
    [onFiltersChange, criteria.filters]
  );

  useEffect(() => {
    setTempCriteria(criteria);
  }, [criteria]);

  return (
    <>
      <FiltersBarButton
        isActive={hasActiveFilters}
        onClick={handleOpenMenu}
        disabled={isFilteringDisabled}
        dataTest="more-filters-button"
      >
        <Typography variant="body1Bold" component="span" sx={{ padding: 0, margin: 0 }}>
          {t('More Filters')}
          {hasActiveFilters && ` +${activeFiltersCount}`}
        </Typography>

        <Add aria-label="More filters menu" />
      </FiltersBarButton>

      <MoreFiltersMenu
        anchorEl={anchorEl}
        open={!!anchorEl}
        filters={filters}
        criteria={tempCriteria}
        onClose={handleCloseAndReset}
        onFilterClear={handleClearFilters}
        onFilterApply={handleApply}
        onFilterChange={handleOptionChange}
        onFilterChangeImmediately={handleOptionChangeImmediately}
      />
    </>
  );
};
